import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import numeral from 'numeral';

import { makeStyles } from '@material-ui/core/styles';

import { getFormat } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { mediaThisMonthAmt } from 'lib/api/media';

import ContentsWrap from 'components/molecules/ContentsWrap';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SwitchingArea from 'components/organisms/SwitchingArea';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';
import SnackbarWrap from 'components/organisms/Snackbar/SnackbarWrap';

import StatisticsGraph from './StatisticsGraph';
import MediaExternalTable from './MediaExternalTable';
import MediaSindyTable from './MediaSindyTable';
import MediaAdvrtsTable from './MediaAdvrtsTable';
import MediaRankTable from './MediaRankTable';
import MediaAdverTable from './MediaAdverTable';
import MediaManagerTable from './MediaManagerTable';

const useStyles = makeStyles((theme) => ({
  sectionWrap: {
    padding: 24,
    marginBottom: 25,
    backgroundColor: '#fff',
  },
  thisMonthAmt: {
    marginBottom: 10,
    fontSize: 18,
    fontWeight: 'bold',
    color: 'red',
  },
}));

const Statistics = () => {
  const { locale } = useSelector(({ locale }) => ({
    locale: locale.locale,
  }));
  const { t } = useTranslation();
  const classes = useStyles();

  const [initialization, setInitialization] = useState(false);

  const initParam = {
    month: moment().format('M'),
    pltfomTpCode: 'all',
  };

  // 공통 필터에서 초기값 받아왔는지 확인
  const [params, setParams] = useState(initParam);
  const [thisMonthAmt, setThisMonthAmt] = useState(0);
  const [infoMessage, setInfoMessage] = useState(false);
  const [list] = useState({
    month: [
      {
        CODE_ID: moment().format('M'),
        CODE_DESC: moment().format('M') + t('월'),
      },
      {
        CODE_ID: moment().add(-1, 'month').format('M'),
        CODE_DESC: moment().add(-1, 'month').format('M') + t('월'),
      },
      {
        CODE_ID: moment().add(-2, 'month').format('M'),
        CODE_DESC: moment().add(-2, 'month').format('M') + t('월'),
      },
    ],
  });
  const initSnackbar = {
    open: false,
    variant: '',
    message: '',
  };
  const [snackbar, setSnackbar] = useState(initSnackbar);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [params]);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  const getData = () => {
    const { sDate, eDate } = params;
    const format = getFormat(locale);

    setInfoMessage(
      t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      })
    );

    mediaThisMonthAmt({ ...paramConverter(params) })
      .then((response) => {
        const { data } = response.data;
        setThisMonthAmt(data.thisMonthAmt);
      })
      .catch((error) => {
        setSnackbar({
          ...snackbar,
          open: true,
          variant: 'error',
          message: error,
        });
      });
  };

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParam);
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  /**
   * 스낵바 닫는 함수
   * 연속 클릭이 들어왔을 때는 기존 스낵바를 유지
   * @param event
   * @param reason
   */
  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({
      ...snackbar,
      open: false,
      message: '',
    });
  };

  return (
    <>
      <div className={classes.sectionWrap}>
        <div className={classes.thisMonthAmt}>
          {moment().format('M')}월 예상 지출금액 :
          {numeral(thisMonthAmt).format('#,##0')}
        </div>
        <StatisticsGraph params={params} />
      </div>
      <div className={classes.sectionWrap}>
        <div>
          <NewFilterWrap handleOnReset={handleOnReset} loading={false}>
            <NewCommonFilter
              initialization={initialization}
              getParam={getParam}
              params={params}
              useDate={false}
              useDisplayFilter={false}
              useExternalFilter={false}
            />
            <div className={classes.root}>
              <FilterCollection
                label={t('날짜선택')}
                name="month"
                params={params}
                value={params.month}
                type="select"
                list={list.month}
                callBack={getParam}
                labelKey="CODE_DESC"
                valueKey="CODE_ID"
              />
            </div>
          </NewFilterWrap>
        </div>
        <SwitchingArea
          title={
            <>
              <InfoTitleArea
                title={t('최근30일 기준 데이터')}
                tooltipTitle={infoMessage}
                iconType="fainfoCircle"
                iconColor="graph"
              />
            </>
          }
        >
          <div style={{ display: 'flex' }}>
            <div style={{ marginRight: 10, width: '50%' }}>
              <MediaExternalTable params={params} />
            </div>
            <div style={{ marginLeft: 10, width: '30%' }}>
              <MediaAdverTable params={params} />
            </div>
            <div style={{ marginLeft: 10, width: '20%' }}>
              <MediaSindyTable params={params} />
            </div>
          </div>
        </SwitchingArea>
        <SwitchingArea title={t('어제 지출금액 TOP 20')}>
          <div>
            <MediaAdvrtsTable params={params} />
          </div>
          <ContentsWrap contentsCount={2}>
            <div>
              <MediaRankTable params={params} />
            </div>
            <div>
              <MediaManagerTable params={params} />
            </div>
          </ContentsWrap>
        </SwitchingArea>
      </div>
      <SnackbarWrap
        open={snackbar.open}
        variant={snackbar.variant}
        message={snackbar.message}
        onClose={handleSnackBarClose}
      />
    </>
  );
};

export default Statistics;
